// Shared Open-Questions (TBD) helpers — used by the editor's
// open-questions section controller AND the wizard's nav/next-TBD
// bookkeeping. Kept in one module so the scan logic and the authored-
// question extraction never drift between the two surfaces.

// A value is a TBD placeholder if it contains a whole-word "TBD" or a
// "??" marker. Gate the scan on this before the (cheaper) extraction.
export const TBD_MARKER_RX = /\bTBD\b|\?\?/i

// Pull the authored question XXX out of a placeholder. The question is
// bracketed by a START marker (TBD / TBD: / ??) and an END marker
// (TBD / ?? / ?), which may sit ANYWHERE in the value — often embedded,
// e.g. "IHSS (??How many … month??)". All the syntaxes collapse to XXX?:
//   "TBD XXX TBD"  → "XXX?"
//   "TBD XXX ?"    → "XXX?"
//   "?? XXX ??"    → "XXX?"
//   "TBD: XXX??"   → "XXX?"   (natural variant)
// Returns null when there's no real sentence between the markers (bare
// "TBD", "??", a "(role)" parenthetical, …) so callers fall back to the
// humanized field name.
export function extractTbdQuestion(value) {
  const raw = String(value == null ? "" : value)
  if (!raw.trim()) return null
  // Ordered most-specific first; the paired markers (?? X ??, TBD X TBD)
  // win over the single-char "?" end so "TBD X ?" doesn't cut a paired
  // question short. Each is unanchored → matches embedded markers.
  const patterns = [
    /\?\?\s*(.+?)\s*\?\?/s,                // ?? X ??
    /\btbd\b\s*:?\s*(.+?)\s*\btbd\b/is,    // TBD X TBD
    /\btbd\b\s*:?\s*(.+?)\s*\?\?/is,       // TBD X ??
    /\?\?\s*(.+?)\s*\?/s,                  // ?? X ?
    /\btbd\b\s*:?\s*(.+?)\s*\?/is          // TBD X ?
  ]
  for (const rx of patterns) {
    const m = raw.match(rx)
    if (!m) continue
    let q = m[1]
      .replace(/^[\s([{]+/, "")        // drop leading space / opening bracket
      .replace(/[?？\s)\]}]+$/, "")     // drop trailing markers / spaces / closers
      .trim()
    if (!q || /^tbd$/i.test(q)) continue
    if (/^\([^)]*\)$/.test(q)) continue   // "(Pediatrician)" role-only
    if (!/[a-z]/i.test(q)) continue        // must contain letters
    if (!/\s/.test(q)) continue            // a single token isn't a question
    return q + "?"
  }
  return null
}

// Top-level plan key → editor/wizard section id, so an open question can
// link back to the section it lives in.
function sectionForKey(k) {
  if (k === "client" || k === "facilitator") return "client"
  if (k === "communicationChart" || k === "communicationStyle") return "communication"
  const known = new Set([
    "about", "circleOfSupport", "wonderfulThings", "notSoFavorites",
    "dayInLife", "resourceCalendar", "important", "bigDreams",
    "supportMatch", "working", "barriers", "goals", "currentServices",
    "unmetNeeds"
  ])
  return known.has(k) ? k : null
}

function humanizeFieldName(path) {
  const last = path[path.length - 1]
  return String(last)
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, c => c.toUpperCase())
    .trim()
}

// Walk the plan for any string value carrying a TBD marker. Returns a
// flat list of { path, sectionId, question, value }. The `question` is
// the authored sentence when one is present, else the field name.
export function findOpenQuestions(plan, base = [], sectionId = null) {
  const out = []
  for (const [k, v] of Object.entries(plan || {})) {
    if (k === "id" || k === "meta") continue
    // Skip the legacy plan.unmetNeeds array at the root: the Unmet Needs section
    // is DERIVED from Goals & Barriers (those TBDs are already surfaced there),
    // and this stale array isn't editable in the current UI — scanning it just
    // produced a phantom open question / badge the user can't resolve.
    if (base.length === 0 && k === "unmetNeeds") continue
    const path = [...base, k]
    const sid = sectionId || sectionForKey(k)
    if (typeof v === "string") {
      if (TBD_MARKER_RX.test(v)) {
        out.push({ path, sectionId: sid, question: extractTbdQuestion(v) || humanizeFieldName(path), value: v })
      }
    } else if (Array.isArray(v)) {
      v.forEach(it => {
        if (typeof it === "string") {
          if (TBD_MARKER_RX.test(it)) out.push({
            path: [...path, "(item)"], sectionId: sid,
            question: extractTbdQuestion(it) || humanizeFieldName([...path, "(item)"]),
            value: it
          })
        } else if (it && typeof it === "object") {
          out.push(...findOpenQuestions(it, [...path, String(it.id || "")], sid))
        }
      })
    } else if (v && typeof v === "object") {
      out.push(...findOpenQuestions(v, path, sid))
    }
  }
  // At the root, fold in explicitly flagged fields (plan.meta.flags[path] ===
  // true). The flag is a NON-destructive "to be determined" marker that does
  // not write "TBD" into the value, so surface it here too — with whatever
  // text the field currently holds — deduped against value-marker hits.
  if (base.length === 0 && plan && plan.meta && plan.meta.flags) {
    const seen = new Set(out.map(o => o.path.join(".")))
    for (const [flagPath, on] of Object.entries(plan.meta.flags)) {
      if (!on || seen.has(flagPath)) continue
      const parts = flagPath.split(".")
      let val = plan
      for (const p of parts) val = (val == null ? undefined : val[p])
      out.push({
        path: parts,
        sectionId: sectionForKey(parts[0]),
        question: humanizeFieldName(parts),
        value: typeof val === "string" ? val : ""
      })
    }
  }
  // Missing required attachments are "things we need" too — surface a missing
  // life photo as an open item so it lands in the Open Questions list.
  if (base.length === 0) {
    const c = plan && plan.client
    const hasPhoto = !!(c && (c.lifePhoto || (c.lifePhotoCaption && String(c.lifePhotoCaption).trim())))
    if (!hasPhoto) {
      out.push({
        path: ["client", "lifePhoto"],
        sectionId: "client",
        question: "Life photo still needed",
        value: ""
      })
    }
  }
  return out
}

// The client's first name for parent-facing copy — or "your child" when
// it's blank / itself a TBD.
export function childName(plan) {
  const f = ((plan && plan.client && plan.client.firstName) || "").trim()
  return f && !TBD_MARKER_RX.test(f) ? f : "your child"
}

// Fill "[FirstName]" / "[First Name]" / "{firstName}" tokens in a single
// question with the client's real first name (left intact when unknown).
export function fillNameTokens(text, plan) {
  const first = ((plan && plan.client && plan.client.firstName) || "").trim()
  const t = String(text == null ? "" : text)
  if (!first || TBD_MARKER_RX.test(first)) return t
  return t.replace(/\[\s*first[\s_]*name\s*\]|\{\{?\s*firstName\s*\}?\}/gi, first)
}

// Merge the (editable) template with the scanned questions. Tokens:
//   {{child}} / {{firstName}} → the client's first name
//   {{questions}}            → numbered list (appended if the token is
//                              absent from the body)
// Returns { subject, body } ready for a mailto.
export function composeOpenQEmail(template, plan, items) {
  const who = childName(plan)
  const nameRx = /\{\{\s*(?:child|firstname|first_name)\s*\}\}/gi
  const list = (items || [])
    .map((it, i) => `${i + 1}. ${fillNameTokens(it.question, plan)}\n   Answer: `)
    .join("\n")
  const subject = String((template && template.subject) || "").replace(nameRx, who)
  let body = String((template && template.body) || "").replace(nameRx, who)
  if (/\{\{\s*questions\s*\}\}/i.test(body)) {
    body = body.replace(/\{\{\s*questions\s*\}\}/gi, list)
  } else {
    body = body.replace(/\s*$/, "") + "\n\n" + list
  }
  return { subject, body }
}
